import CheerzApiCart, { CheerzApiCartArticle } from "@Types/api/cart";
import CartContent, { Article } from "@Types/cart";
import { convertPriceFromApiToContent, convertPriceFromContentToApi } from "@Utils/cart/price";
import {
  convertShippingOptionFromApiToContent,
  convertShippingOptionFromContentToApi,
} from "@Utils/cart/shipping";

export const convertCartArticleFromApiToContent = (articles: CheerzApiCartArticle[]): Article[] => {
  return articles.map((article) => {
    return {
      additionalOptions: article.additional_options.map((option) => {
        return {
          name: option.name ? option.name : undefined,
          optionValueTag: option.option_value_tag,
          productOptionTag: option.product_option_tag,
          quantity: option.quantity,
          tag: option.tag,
          title: option.title ? option.title : undefined,
          total: convertPriceFromApiToContent(option.total),
        };
      }),

      contentDeviceTag: article.content_device_tag,
      createdDate: article.created_at,
      currency: article.currency,
      customerImageUrl: article.customer_image_url ? article.customer_image_url : undefined,
      gift: article.gift ? true : false,
      id: article.id,
      pagesCount: article.pages_count,
      photoCount: article.photo_count,

      product: {
        articleBehaviorTag: article.product.article_behavior_tag,
        deprecated: article.product.deprecated,
        isKustomAvailableOnMobile: article.product.kustom_web_mobile
          ? article.product.kustom_web_mobile
          : false,
        isQuantityUpdatable: article.product.quantity_is_updatable,
        kustomPageNamePlural: article.product.kustom_page_name_plural,
        kustomPageNameSingular: article.product.kustom_page_name_singular,
        name: article.product.name,
        outOfStock: article.product.out_of_stock,
        priceCents: article.product.price_cents,
        productImage: {
          big: article.product.product_image["3x"],
          medium: article.product.product_image["2x"],
          small: article.product.product_image["1x"],
        },
        skipSelection: article.product.skip_selection,
        statProductGroupTag: article.product.stat_product_group_tag,
        tag: article.product.tag,
      },

      quantity: article.quantity,
      recovered: article.recovered,
      selectionContent: article.selection_content ? article.selection_content : undefined,
      title: article.title ? article.title : undefined,
      total: convertPriceFromApiToContent(article.total),
      updatedDate: article.updated_at,
    };
  });
};

export function convertCartFromContentToApi(cartContent: CartContent): CheerzApiCart {
  const cartJson: CheerzApiCart = {
    articles: cartContent.articles.map((article) => {
      return {
        additional_options: article.additionalOptions.map((option) => {
          return {
            name: option.name ? option.name : null,
            option_value_tag: option.optionValueTag,
            product_option_tag: option.productOptionTag,
            quantity: option.quantity,
            tag: option.tag,
            title: option.title ? option.title : null,
            total: convertPriceFromContentToApi(option.total),
          };
        }),

        content_device_tag: article.contentDeviceTag,
        created_at: article.createdDate,
        currency: article.currency,
        customer_image_url: article.customerImageUrl ? article.customerImageUrl : null,
        gift: article.gift,
        id: article.id,
        pages_count: article.pagesCount,
        photo_count: article.photoCount,

        product: {
          article_behavior_tag: article.product.articleBehaviorTag,
          deprecated: article.product.deprecated,
          kustom_page_name_plural: article.product.kustomPageNamePlural,
          kustom_page_name_singular: article.product.kustomPageNameSingular,
          kustom_web_mobile: article.product.isKustomAvailableOnMobile,
          name: article.product.name,
          out_of_stock: article.product.outOfStock,
          price_cents: article.product.priceCents,
          product_image: {
            "1x": article.product.productImage.small,
            "2x": article.product.productImage.medium,
            "3x": article.product.productImage.big,
          },
          quantity_is_updatable: article.product.isQuantityUpdatable,
          skip_selection: article.product.skipSelection,
          stat_product_group_tag: article.product.statProductGroupTag,
          tag: article.product.tag,
        },

        quantity: article.quantity,
        recovered: article.recovered,
        selection_content: article.selectionContent ? article.selectionContent : null,
        title: article.title ? article.title : null,
        total: convertPriceFromContentToApi(article.total),
        updated_at: article.updatedDate,
      };
    }),

    currency: cartContent.currency,
    delivery_dates: cartContent.deliveryDates,
    free_shipping_config: cartContent.freeShippingConfig
      ? {
          amount_until_promo: convertPriceFromContentToApi(
            cartContent.freeShippingConfig.amountUntilPromo
          ),
          appliable_promo: cartContent.freeShippingConfig.appliablePromo,
          promo_code: cartContent.freeShippingConfig.promoCode,
          promo_code_applied: cartContent.freeShippingConfig.promoCodeApplied,
          promo_trigger_amount: convertPriceFromContentToApi(
            cartContent.freeShippingConfig.promoTriggerAmount
          ),
        }
      : null,
    gift_note: {
      available: cartContent.giftNote.available,
      content: cartContent.giftNote.content ? cartContent.giftNote.content : null,
    },
    locked_during_payment: cartContent.lockedDuringPayment,
    order_number: cartContent.orderNumber,
    promo_code: cartContent.promoCode ? cartContent.promoCode : null,
    promo_total: convertPriceFromContentToApi(cartContent.promoTotal),
    revenue: cartContent.revenue,
    shippable: cartContent.shippable,
    shipping_options: {
      express: convertShippingOptionFromContentToApi(cartContent.shippingOptions.home.express),
      mondial_relay: {
        express: convertShippingOptionFromContentToApi(
          cartContent.shippingOptions.mondialRelay.express
        ),
        normal: convertShippingOptionFromContentToApi(
          cartContent.shippingOptions.mondialRelay.normal
        ),
      },
      normal: convertShippingOptionFromContentToApi(cartContent.shippingOptions.home.normal),
    },
    shipping_total: convertPriceFromContentToApi(cartContent.shippingTotal),
    sub_total: convertPriceFromContentToApi(cartContent.subTotal),
    tax: cartContent.tax,
    total: convertPriceFromContentToApi(cartContent.total),
  };

  return cartJson;
}

export function convertCartFromApiToContent(jsonContent: CheerzApiCart): CartContent {
  const cartContent: CartContent = {
    articles: convertCartArticleFromApiToContent(jsonContent.articles),

    currency: jsonContent.currency,
    deliveryDates: jsonContent.delivery_dates,
    freeShippingConfig: jsonContent.free_shipping_config
      ? {
          amountUntilPromo: convertPriceFromApiToContent(
            jsonContent.free_shipping_config.amount_until_promo
          ),
          appliablePromo: jsonContent.free_shipping_config.appliable_promo,
          promoCode: jsonContent.free_shipping_config.promo_code,
          promoCodeApplied: jsonContent.free_shipping_config.promo_code_applied,
          promoTriggerAmount: convertPriceFromApiToContent(
            jsonContent.free_shipping_config.promo_trigger_amount
          ),
        }
      : undefined,
    giftNote: {
      available: jsonContent.gift_note.available,
      content: jsonContent.gift_note.content ? jsonContent.gift_note.content : undefined,
    },
    lockedDuringPayment: jsonContent.locked_during_payment,
    orderNumber: jsonContent.order_number,
    promoCode: jsonContent.promo_code ? jsonContent.promo_code : undefined,
    promoTotal: convertPriceFromApiToContent(jsonContent.promo_total),
    revenue: jsonContent.revenue,
    shippable: jsonContent.shippable,
    shippingOptions: {
      home: {
        express: convertShippingOptionFromApiToContent(jsonContent.shipping_options.express),
        normal: convertShippingOptionFromApiToContent(jsonContent.shipping_options.normal),
      },
      mondialRelay: {
        express: convertShippingOptionFromApiToContent(
          jsonContent.shipping_options.mondial_relay.express
        ),
        normal: convertShippingOptionFromApiToContent(
          jsonContent.shipping_options.mondial_relay.normal
        ),
      },
    },
    shippingTotal: convertPriceFromApiToContent(jsonContent.shipping_total),
    subTotal: convertPriceFromApiToContent(jsonContent.sub_total),
    tax: jsonContent.tax,
    total: convertPriceFromApiToContent(jsonContent.total),
  };

  return cartContent;
}
